import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import API from "../../../../app/utils/api";
import { Modal, Tooltip } from "../../../../app/components";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import styled from "styled-components";
import { useScheduleEvents } from "../../../../app/contexts/schedule.events.context";
import PrimaryButton from "../../../../app/components/buttons/primary";
import OutlinedButton from "../../../../app/components/buttons/outlined";
import { getDefaultCloneName } from "../../../../app/utils/cloneHelpers";

// Constants
const CLONE_SUFFIX = " - Clone";
const SOE_NAME_MAX_LENGTH = 50;
const SOE_DESCRIPTION_MAX_LENGTH = 2000;
const CLONE_OPTIONS_INFO = (
  <>
    The core SOE structure (e.g., Name, Description, Visits, Assessments, Visit-Assessment Mapping) is always cloned.
    <br />
    All selected options are included in the clone.
    <br />
    All unselected options are not cloned/set to new SOE defaults.
  </>
);
const CLONE_OPTIONS = {
  cloneProtocolVersionDate: true,
  cloneStudyPlan: true,
  cloneSoeComments: true,
  cloneClinicalStrategy: true,
  cloneCountryCosts: true,
  cloneSiteCosts: true,
};

const CLONE_OPTION_KEYS = Object.keys(CLONE_OPTIONS);

const ALL_CLONE_OPTIONS_TRUE = CLONE_OPTION_KEYS.reduce(
  (acc, key) => ({ ...acc, [key]: true }),
  {},
);
const ALL_CLONE_OPTIONS_FALSE = CLONE_OPTION_KEYS.reduce(
  (acc, key) => ({ ...acc, [key]: false }),
  {},
);

/*
  This component is a modal that allows the user to clone a schedule of events.
  It uses the schedule events context to handle the cloning of the schedule of events.
  The parent component must pass a callback function to handle the schedule of events parameters.
*/
const CloneScheduleOfEvent = ({
  open = false,
  onClose = (noop) => noop,
  soe,
}) => {
  const { fetchSOEStudy } = useScheduleEvents();
  const navigate = useNavigate();
  const classes = useStyles();
  const { id } = useParams();
  const [formValid, setFormValid] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const [SOEEventParameters, setSOEEventParameters] = useState({
    scheduleOfEventsName: getDefaultCloneName(soe.scheduleOfEventsName || "", CLONE_SUFFIX, SOE_NAME_MAX_LENGTH),
    scheduleOfEventsDescr: soe?.scheduleOfEventsDescr ?? "",
  });
  const [cloneOptions, setCloneOptions] = useState(CLONE_OPTIONS);

  const [errors, setErrors] = useState({});

  useEffect(() => {
    handleChange("scheduleOfEventsName", getDefaultCloneName(soe.scheduleOfEventsName || "", CLONE_SUFFIX, SOE_NAME_MAX_LENGTH));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (open) {
      setCloneOptions(CLONE_OPTIONS);
    }
  }, [open]);

  useEffect(() => {
    if (
      SOEEventParameters["scheduleOfEventsDescr"] &&
      SOEEventParameters["scheduleOfEventsName"] &&
      !errors["scheduleOfEventsDescr"] &&
      !errors["scheduleOfEventsName"]
    ) {
      setFormValid(true);
    } else {
      setFormValid(false);
    }
  }, [SOEEventParameters, errors]);

  const handleChange = (key, value) => {
    setSOEEventParameters({ ...SOEEventParameters, [key]: value });
    
    // Clear any existing errors for this field first
    let newErrors = { ...errors, [key]: "" };
    
    // Check if field is empty
    if (!value) {
      newErrors[key] = "This field is required";
    }
    
    if (key === "scheduleOfEventsName") {
      // Check length limit
      if (value.length > SOE_NAME_MAX_LENGTH) {
        newErrors[key] = `SOE name cannot exceed ${SOE_NAME_MAX_LENGTH} characters`;
      }
      // Check if name contains at least 1 non-whitespace character
      else if (!value.replace(/\s/g, "").length) {
        newErrors[key] = "SOE name must contain at least 1 character";
      }
      // Check for uniqueness
      else {
        const isDuplicate = soe.scheduleOfEvents?.some((sp) => 
          sp?.scheduleOfEventsName?.toLowerCase().trim() === value.toLowerCase().trim()
        );
        if (isDuplicate) {
          newErrors[key] = "Schedule of Events name must be unique";
        }
      }
    }
    
    if (key === "scheduleOfEventsDescr") {
      // Check length limit
      if (value.length > SOE_DESCRIPTION_MAX_LENGTH) {
        newErrors[key] = `SOE description cannot exceed ${SOE_DESCRIPTION_MAX_LENGTH} characters`;
      }
      // Check if description contains at least 1 non-whitespace character
      else if (!value.replace(/\s/g, "").length) {
        newErrors[key] = "SOE description must contain at least 1 character";
      }
    }
    
    setErrors(newErrors);
  };

  const handleCloneAndEdit = () => {
    setSaveLoading(true);
    const { scheduleOfEventsId } = soe;
    const new_soe = {
      scheduleOfEventsId,
      studyId: parseInt(id),
      scheduleOfEventsName: SOEEventParameters.scheduleOfEventsName,
      scheduleOfEventsDescr: SOEEventParameters.scheduleOfEventsDescr,
      cloneProtocolVersionDate: cloneOptions.cloneProtocolVersionDate ? 1 : 0,
      cloneStudyPlan: cloneOptions.cloneStudyPlan ? 1 : 0,
      cloneSoeComments: cloneOptions.cloneSoeComments ? 1 : 0,
      cloneClinicalStrategy: cloneOptions.cloneClinicalStrategy ? 1 : 0,
      cloneCountryCosts: cloneOptions.cloneCountryCosts ? 1 : 0,
      cloneSiteCosts: cloneOptions.cloneSiteCosts ? 1 : 0,
    };
    API.createNewSOEClone(new_soe, id)
      .then(async (res) => {
        setSaveLoading(false);
        if (res.data[0].Id) {
          navigate(`/ctpo/study-plans/${id}/soe/${res.data[0].Id}`);
          await fetchSOEStudy(id);
        }
      })
      .catch((err) => {
        console.error(err);
        setSaveLoading(false);
      });
  };

  const handleCloneOptionChange = (key, checked) => {
    if (key === "cloneStudyPlan" && !checked) {
      setCloneOptions((prev) => ({
        ...prev,
        cloneStudyPlan: false,
        cloneCountryCosts: false,
        cloneSiteCosts: false,
      }));
      return;
    }

    setCloneOptions((prev) => ({
      ...prev,
      [key]: checked,
    }));
  };

  const allCloneOptionsSelected = CLONE_OPTION_KEYS.every(
    (key) => cloneOptions[key],
  );
  const someCloneOptionsSelected = CLONE_OPTION_KEYS.some(
    (key) => cloneOptions[key],
  );

  const handleSelectAllCloneOptions = (checked) => {
    setCloneOptions(checked ? ALL_CLONE_OPTIONS_TRUE : ALL_CLONE_OPTIONS_FALSE);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="clone SOE"
      aria-describedby="clone a SOE"
      title="Clone SOE"
      sx={{
        width: "min(560px, 95vw)",
      }}
    >
      <Container>
        <TextField
          value={SOEEventParameters.scheduleOfEventsName}
          onChange={(e) => handleChange("scheduleOfEventsName", e.target.value)}
          label="SOE Name"
          variant="outlined"
          InputLabelProps={{ classes: classes }}
          fullWidth
          required
          error={!!errors.scheduleOfEventsName}
          inputProps={{
            maxLength: SOE_NAME_MAX_LENGTH,
          }}
          helperText={
            <AlignBetween>
              <span>{errors.scheduleOfEventsName}</span>
              <span>{`${
                (SOEEventParameters.scheduleOfEventsName || "").length
              } / ${SOE_NAME_MAX_LENGTH}`}</span>
            </AlignBetween>
          }
        />
        <TextField
          value={SOEEventParameters.scheduleOfEventsDescr}
          onChange={(e) =>
            handleChange("scheduleOfEventsDescr", e.target.value)
          }
          label="SOE Description"
          variant="outlined"
          InputLabelProps={{ classes: classes }}
          multiline
          fullWidth
          required
          inputProps={{
            maxLength: SOE_DESCRIPTION_MAX_LENGTH,
          }}
          rows={4}
          error={!!errors.scheduleOfEventsDescr}
          helperText={
            <AlignBetween>
              <span>{errors.scheduleOfEventsDescr}</span>
              <span>{`${
                (SOEEventParameters.scheduleOfEventsDescr || "").length
              } / ${SOE_DESCRIPTION_MAX_LENGTH}`}</span>
            </AlignBetween>
          }
        />
        <CloneOptionsSection>
          <CloneOptionsLabelRow>
            <Typography variant="body2">Select options to clone:</Typography>
            <Tooltip info={CLONE_OPTIONS_INFO} zIndex={1400} />
          </CloneOptionsLabelRow>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={allCloneOptionsSelected}
                indeterminate={
                  someCloneOptionsSelected && !allCloneOptionsSelected
                }
                onChange={(e) =>
                  handleSelectAllCloneOptions(e.target.checked)
                }
              />
            }
            label="Select All"
          />
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={cloneOptions.cloneProtocolVersionDate}
                onChange={(e) =>
                  handleCloneOptionChange(
                    "cloneProtocolVersionDate",
                    e.target.checked,
                  )
                }
              />
            }
            label="Protocol Version/Date"
          />
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={cloneOptions.cloneStudyPlan}
                onChange={(e) =>
                  handleCloneOptionChange("cloneStudyPlan", e.target.checked)
                }
              />
            }
            label="Study Plan"
          />
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={cloneOptions.cloneSoeComments}
                onChange={(e) =>
                  handleCloneOptionChange("cloneSoeComments", e.target.checked)
                }
              />
            }
            label="SOE Comments"
          />
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={cloneOptions.cloneClinicalStrategy}
                onChange={(e) =>
                  handleCloneOptionChange(
                    "cloneClinicalStrategy",
                    e.target.checked,
                  )
                }
              />
            }
            label="Clinical Strategy"
          />
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={cloneOptions.cloneCountryCosts}
                disabled={!cloneOptions.cloneStudyPlan}
                onChange={(e) =>
                  handleCloneOptionChange("cloneCountryCosts", e.target.checked)
                }
              />
            }
            label="Country Costs"
          />
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={cloneOptions.cloneSiteCosts}
                disabled={!cloneOptions.cloneStudyPlan}
                onChange={(e) =>
                  handleCloneOptionChange("cloneSiteCosts", e.target.checked)
                }
              />
            }
            label="Site Costs"
          />
        </CloneOptionsSection>
        <SideBySide>
          <OutlinedButton onClick={onClose}>Cancel </OutlinedButton>
          <PrimaryButton
            onClick={handleCloneAndEdit}
            disabled={saveLoading || !formValid}
          >
            Clone & Edit Details
          </PrimaryButton>
        </SideBySide>
      </Container>
    </Modal>
  );
};

export default CloneScheduleOfEvent;

const Container = styled.div`
  display: grid;
  grid-gap: 1.25em;
  max-height: 72vh;
  overflow-y: auto;
  padding-top: 0.75em;
  padding-right: 0.35em;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  &::-webkit-scrollbar-thumb {
    background: #888;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;
const SideBySide = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1em;
  position: sticky;
  bottom: 0;
  background: white;
  padding-top: 0.5em;
  z-index: 1;
`;

const AlignBetween = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;
const CloneOptionsSection = styled.div`
  display: grid;
  gap: 0.35em;
`;

const CloneOptionsLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.15em;
`;

const useStyles = makeStyles((theme) => ({
  asterisk: {
    color: "red",
  },
}));